/**
 * connectProvider — THE single connect/scope-grant flow for the whole app.
 *
 * Every "Connect <provider>" / "Enable <capability>" / "add permissions" entry
 * point (onboarding, contacts, drive, calendar, email, chat chips, the
 * integrations modal, scope-upgrade prompts) MUST call this. There is no other
 * connect path — the old `triggerOAuthPopup` is gone, and no component builds its
 * own `/integrations/.../auth` request. Fixing the connect UX here fixes it
 * everywhere; that's the whole point of having one function.
 *
 * It is a same-window redirect (no popup): we fetch the provider authorize URL
 * from the backend (which owns scope resolution, never include_granted_scopes,
 * and — when `integrationId` is present — the incremental-consent flow:
 * login_hint + no forced prompt, so an EXPANSION skips the account chooser and
 * only consents the new scope) and navigate to it. The backend 302s back to
 * `redirectUrl` with integration_connected/calendar_connected, which the
 * SessionProvider + App return handlers consume.
 *
 * Pre-redirect side effects (e.g. onboarding's convert-from-demo marker) belong
 * in the caller, BEFORE calling this — the page unloads on redirect.
 */

import { apiFetch } from '@/lib/apiClient'

export interface ConnectProviderOptions {
  /** Provider key, e.g. 'google' | 'microsoft' | 'shopify'. */
  provider: string
  /** Session auth token for the authorize-URL fetch. */
  authToken: string | null
  /**
   * Capability being granted (ADR 017 D4), forwarded as `?capability=`. The
   * backend resolves the explicit scope union; the frontend never names raw
   * OAuth scopes.
   */
  capability?: string
  /**
   * Existing integration being EXPANDED (incremental consent). Forwarded as
   * `?integrationId=`. Its presence is what makes the backend run the incremental
   * flow (login_hint + drop the forced prompt) and union the account's already-
   * granted scopes so nothing is dropped.
   */
  integrationId?: string
  /** Active conversation id — carried into the per-provider oauth_state row. */
  conversationId?: string
  /** Shopify store subdomain (e.g. 'mystore'). */
  shopDomain?: string
  /**
   * Where the backend should 302 back to after the grant. Defaults to the
   * current in-app URL so the user returns exactly where they were (the in-app
   * onboarding connect step relies on this default — there is no /welcome page).
   */
  redirectUrl?: string
  /**
   * Email to send as `login_hint` so the provider targets THIS account and skips
   * its account chooser — for connecting the account the user is already signed in
   * as (e.g. onboarding right after SSO), where re-picking is redundant. OMIT it
   * for "add a different account" (Manage Accounts) so the chooser still appears.
   * The backend also drops Microsoft's `select_account` when a login_hint is set.
   */
  loginHint?: string
}

/**
 * Start a connect/scope-grant: fetch the authorize URL and navigate to it.
 *
 * Resolves only if the fetch fails (the success path navigates away and never
 * resolves). On failure it throws so the caller can surface an error and restore
 * its button state.
 */
export async function connectProvider(opts: ConnectProviderOptions): Promise<never | void> {
  const params = new URLSearchParams({
    redirectUrl: opts.redirectUrl ?? window.location.href,
  })
  if (opts.capability) params.set('capability', opts.capability)
  if (opts.integrationId) params.set('integrationId', opts.integrationId)
  if (opts.conversationId) params.set('conversationId', opts.conversationId)
  if (opts.loginHint) params.set('login_hint', opts.loginHint)
  if (opts.provider === 'shopify' && opts.shopDomain) {
    params.set('shopDomain', opts.shopDomain)
  }

  const data = (await apiFetch(`/integrations/${opts.provider}/auth?${params.toString()}`, {
    authToken: opts.authToken ?? undefined,
  })) as { authUrl: string }

  window.location.href = data.authUrl
}
